'use client'

import { Checkbox } from '@components/primitives/Checkbox'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { STATUS_LABELS } from '@modules/trials/search/const'
import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'

export function TrialStatusFilterRefinementList({
  defaultOpen = false,
  inputClassName,
}: {
  defaultOpen?: boolean
  inputClassName?: string
}) {
  const searchParams = useSearchParams()
  const router = useRouter()

  // Parse the current status values from the URL
  const currentStatusStr = searchParams?.get('overallStatus') ?? ''
  const initialStatus = currentStatusStr ? currentStatusStr.split(',') : []

  // Create local state for faster UI updates
  const [selectedStatus, setSelectedStatus] = useState<string[]>(initialStatus)

  // Sync URL with local state when searchParams change
  useEffect(() => {
    const statusFromUrl = searchParams?.get('overallStatus') ?? ''
    const statusArray = statusFromUrl ? statusFromUrl.split(',') : []
    setSelectedStatus(statusArray)
  }, [searchParams])

  // State to handle open/closed list
  const [isOpen, setIsOpen] = useState(defaultOpen)

  const handleRefinement = (value: string) => {
    const params = new URLSearchParams(searchParams?.toString() ?? '')

    if (params.has('page')) {
      params.delete('page')
    }

    // Update local state immediately for fast UI feedback
    let updatedStatus: string[]

    if (selectedStatus.includes(value)) {
      // Remove the status if already selected
      updatedStatus = selectedStatus.filter((status) => status !== value)
    } else {
      // Add the new status while keeping existing ones
      updatedStatus = [...selectedStatus, value]
    }

    // Update local state first for immediate UI feedback
    setSelectedStatus(updatedStatus)

    // Then update URL
    if (updatedStatus.length > 0) {
      params.set('overallStatus', updatedStatus.join(','))
    } else {
      params.delete('overallStatus')
    }

    // Update the URL with a soft navigation
    router.push(`?${params.toString()}`, { scroll: false })
  }

  return (
    <div
      className={classMerge(
        'w-full rounded-lg border-grey200 bg-white',
        inputClassName,
      )}
    >
      <div
        className='flex w-full cursor-pointer items-center justify-between self-center'
        onClick={() => setIsOpen(!isOpen)}
      >
        <Text
          className='text-neutral900 antialiased'
          element='p'
          styleName='p-small-strong'
        >
          Trial Status
        </Text>
      </div>
      <ul className='pb-2'>
        {Object.entries(STATUS_LABELS).map(([key, label]) => (
          <li key={key}>
            <label
              className='flex cursor-pointer items-center py-0.5'
              onClick={(e) => e.stopPropagation()}
            >
              <Checkbox
                checked={selectedStatus.includes(key)}
                className='peer disabled:cursor-not-allowed disabled:bg-primary300 disabled:opacity-20'
                id={key}
                onChange={() => {
                  handleRefinement(key)
                  trackEvent(
                    TrackingEvents.SEARCH_LANDER_SECONDARY_FILTER_CLICKED,
                    {
                      val: label,
                    },
                  )
                }}
              />
              <Text
                className='ml-2 cursor-pointer select-none text-neutral900 peer-disabled:cursor-not-allowed peer-disabled:opacity-20'
                styleName='p-small'
              >
                {label}
              </Text>
            </label>
          </li>
        ))}
      </ul>
    </div>
  )
}
